import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { LeafLogo } from "@/components/common/LeafLogo";
import { MapPin, Mail, Phone } from "lucide-react";

const defaultQuick = [
  { to: "/about", key: "navAbout" }, { to: "/office-bearers", key: "navBearers" },
  { to: "/constitution", key: "navConstitution" }, { to: "/districts", key: "navDistricts" },
  { to: "/gr-notices", key: "navGr" }, { to: "/gapl", key: "navGapl" },
];
const defaultLegal = [
  { to: "/legal/privacy", key: "privacy" }, { to: "/legal/terms", key: "terms" },
  { to: "/legal/refund", key: "refund" }, { to: "/legal/ad-policy", key: "adPolicy" },
];

export const Footer = () => {
  const { t, lang, site } = useLang();
  const [footer, setFooter] = useState(null);
  useEffect(() => { api.footerSettings().then(setFooter).catch(() => {}); }, []);
  const customTagline = footer && footer.show ? (lang === "gu" ? footer.tagline_gu : footer.tagline_en) : "";
  const tagline = customTagline || t("assocTagline");
  const showTagline = !footer || footer.show;

  const info = site?.site_info || {};
  const assocShort = (lang === "gu" ? info.assoc_short_gu : info.assoc_short_en) || t("assocShort");
  const assocName = (lang === "gu" ? info.assoc_name_gu : info.assoc_name_en) || t("assocName");
  const address = (lang === "gu" ? info.address_gu : info.address_en) || t("address");

  const navFooter = site?.nav_footer;
  const quick = navFooter?.quick?.length
    ? navFooter.quick.map((q) => ({ to: q.to, label: lang === "gu" ? q.label_gu : q.label_en }))
    : defaultQuick.map((q) => ({ to: q.to, label: t(q.key) }));
  const legal = navFooter?.legal?.length
    ? navFooter.legal.map((q) => ({ to: q.to, label: lang === "gu" ? q.label_gu : q.label_en }))
    : defaultLegal.map((q) => ({ to: q.to, label: t(q.key) }));

  return (
    <footer className="mt-16 bg-gmoaa-green text-white/90">
      <div className="gmoaa-container grid grid-cols-1 gap-10 py-12 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-3">
            <LeafLogo size={40} />
            <p className="font-serif text-xl font-bold text-white">{assocShort}</p>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-white/70">{assocName}</p>
          {showTagline && <p className="mt-2 text-xs text-gmoaa-gold-light" data-testid="footer-tagline">{tagline}</p>}
        </div>

        <div>
          <h4 className="font-serif text-lg font-semibold text-white">{t("quickLinks")}</h4>
          <ul className="mt-3 space-y-2 text-sm">
            {quick.map((q) => (
              <li key={q.to}><Link to={q.to} className="text-white/70 transition-colors hover:text-gmoaa-gold">{q.label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-serif text-lg font-semibold text-white">{t("legal")}</h4>
          <ul className="mt-3 space-y-2 text-sm">
            {legal.map((q) => (
              <li key={q.to}><Link to={q.to} className="text-white/70 transition-colors hover:text-gmoaa-gold">{q.label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-serif text-lg font-semibold text-white">{t("contactUs")}</h4>
          <ul className="mt-3 space-y-2.5 text-sm text-white/70">
            {info.show_address !== false && <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 shrink-0 text-gmoaa-gold" /> {address}</li>}
            {info.show_email !== false && info.email && <li className="flex items-center gap-2"><Mail size={16} className="text-gmoaa-gold" /> {info.email}</li>}
            {info.show_phone !== false && info.phone && <li className="flex items-center gap-2"><Phone size={16} className="text-gmoaa-gold" /> {info.phone}</li>}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/15">
        <div className="gmoaa-container flex flex-wrap items-center justify-between gap-2 py-4 text-xs text-white/60">
          <p>© {new Date().getFullYear()} {assocShort}. {t("rights")}</p>
          {info.website && <p>{info.website}</p>}
        </div>
      </div>
    </footer>
  );
};
